import * as errors from '@attain-apps/errors'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as core from '../../core'
import * as quiver from '../quiver'

import { Record } from './record'

/** A space report. */
export type Report = core.common.Identified<Report.Id> & core.common.Named

export namespace Report {
  /** Identifies a specific space report. */
  export enum Id {
    AccountList = 'account-list',
    DataExport = 'data-export',
    EmployeeList = 'employee-list',
    RoomLinks = 'room-links',
    RoomSupportingInformation = 'room-supporting-information',
    RoomsByAccount = 'rooms-by-account',
    RoomsByOccupant = 'rooms-by-occupant',
    RoomsByResponsiblePerson = 'rooms-by-responsible-person',
    SpaceSalaryComparison = 'space-salary-comparison',
    UnassignedSponsoredAccounts = 'unassigned-sponsored-accounts',
    UnassignedSponsoredPeople = 'unassigned-sponsored-people',
    ProgressByOrganization = 'progress-by-organization',
    UnclaimedRooms = 'unclaimed-rooms',
  }
  // Needs to be before namespace is defined
  const IDS: ReadonlySet<Id> = new Set(Object.values(Id) as Id[])
  export namespace Id {
    export const ALL: readonly Id[] = Array.from(IDS)
    export const isId = TypeChecker.Factory.oneOf(IDS)
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)
  }

  /**
   * Contains information about all of the {@link Report}
   *
   * NOTE: `globalThis.Record` because `Record` refers to the space record
   * entity in this file
   */
  const LOOKUP: globalThis.Record<Id, Omit<Report, 'id'>> = {
    [Id.AccountList]: {
      name: 'Account List',
    },
    [Id.DataExport]: {
      name: 'Data Export',
    },
    [Id.EmployeeList]: {
      name: 'Employee List',
    },
    [Id.RoomLinks]: {
      name: 'Room Links',
    },
    [Id.RoomSupportingInformation]: {
      name: 'Room Supporting Information',
    },
    [Id.RoomsByAccount]: {
      name: 'Rooms by Account',
    },
    [Id.RoomsByOccupant]: {
      name: 'Rooms by Occupant',
    },
    [Id.RoomsByResponsiblePerson]: {
      name: 'Rooms by Responsible Person',
    },
    [Id.SpaceSalaryComparison]: {
      name: 'Space/Salary Comparison',
    },
    [Id.UnassignedSponsoredAccounts]: {
      name: 'Unassigned Sponsored Accounts',
    },
    [Id.UnassignedSponsoredPeople]: {
      name: 'Unassigned Sponsored People',
    },
    [Id.ProgressByOrganization]: {
      name: 'Progress by Organization',
    },
    [Id.UnclaimedRooms]: {
      name: 'Unclaimed Rooms',
    },
  }

  /** Output format a report can be generated in. */
  export enum Format {
    Html = 'html',
    Excel = 'excel',
    Pdf = 'pdf',
  }
  // Needs to be before namespace is defined
  const FORMATS: ReadonlySet<Format> = new Set(
    Object.values(Format) as Format[],
  )
  export namespace Format {
    // NOTE: `globalThis.Record` because `Record` refers to the space record
    // entity in this file
    const LABELS: Readonly<globalThis.Record<Format, string>> = {
      [Format.Html]: 'HTML',
      [Format.Excel]: 'Excel',
      [Format.Pdf]: 'PDF',
    }

    export const ALL: readonly Format[] = Array.from(FORMATS)
    export const isFormat = TypeChecker.Factory.oneOf(FORMATS)
    export const assertIsFormat: TypeAsserter<Format> =
      TypeAsserter.Factory.from(isFormat)

    export const getLabel = (format: Format): string => LABELS[format]

    /**
     * Formats that can currently be generated.
     *
     * TODO: replace with {@link ALL} once HTML and PDF are supported.
     */
    export const SUPPORTED: readonly Format[] = [Format.Excel]
  }

  /**
   * Returns the requested {@link Report}
   */
  export function get(id: Id): Report {
    return { ...LOOKUP[id], id }
  }

  /**
   * The {@link Report Reports} only Platform Admins, Tenant Admins and Survey
   * Admins can generate
   */
  const REPORTS_REQUIRING_ADMIN_ACCESS: ReadonlySet<Id> = new Set([
    Id.SpaceSalaryComparison,
    Id.ProgressByOrganization,
    Id.UnclaimedRooms,
    Id.RoomsByAccount,
    Id.RoomsByOccupant,
    Id.RoomsByResponsiblePerson,
    Id.RoomLinks,
  ])

  /**
   * Whether the requested {@link Report} can only be generated by Platform
   * Admins, Tenant Admins and Survey Admins
   */
  export function requiresAdminAccess(id: Id): boolean {
    return REPORTS_REQUIRING_ADMIN_ACCESS.has(id)
  }

  /**
   * Returns the name of the requested {@link Report}
   */
  export function getName(id: Id): string {
    return LOOKUP[id].name
  }

  let _list: Report[] | undefined
  /**
   * Returns all {@link Report}
   */
  export function list(): readonly Report[] {
    if (_list) {
      // If we've already created the list before, just return it
      return _list
    }
    // Otherwise, extract the list from the lookup
    _list = Object.entries(LOOKUP).map<Report>(([id, reportWithoutId]) => ({
      ...reportWithoutId,
      id: id as keyof typeof LOOKUP,
    }))
    // Sort the entries by name (ensures a consistent sorting that is also
    // user-friendly)
    _list.sort((a, b) => a.name.localeCompare(b.name))

    return _list
  }

  export type Configuration<TId extends Id = Id> = {
    [Id.AccountList]: Configuration.AccountList
    [Id.DataExport]: Configuration.DataExport
    [Id.EmployeeList]: Configuration.EmployeeList
    [Id.RoomLinks]: Configuration.RoomLinks
    [Id.RoomSupportingInformation]: Configuration.RoomSupportingInformation
    [Id.RoomsByAccount]: Configuration.RoomsByAccount
    [Id.RoomsByOccupant]: Configuration.RoomsByOccupant
    [Id.RoomsByResponsiblePerson]: Configuration.RoomsByResponsiblePerson
    [Id.SpaceSalaryComparison]: Configuration.SpaceSalaryComparison
    [Id.UnassignedSponsoredAccounts]: Configuration.UnassignedSponsoredAccounts
    [Id.UnassignedSponsoredPeople]: Configuration.UnassignedSponsoredPeople
    [Id.ProgressByOrganization]: Configuration.ProgressByOrganization
    [Id.UnclaimedRooms]: Configuration.UnclaimedRooms
  }[TId]
  export namespace Configuration {
    /**
     * Gets the runtime checker for the given {@link Configuration} type, based
     * on the provided {@link Id}.
     */
    export function getTypeChecker<TId extends Id>(
      id: TId,
    ): TypeChecker<Configuration<TId>> {
      return STRICT_TYPE_CHECKERS[id]
    }

    /**
     * Gets the {@link Configuration} a report starts out with, before the user
     * has changed anything
     */
    export function getDefault<TId extends Id>(id: TId): Configuration<TId> {
      return DEFAULTS[id]
    }

    /**
     * The part of a {@link Configuration} that controls Salary Information, for
     * the reports that can include it
     */
    export interface WithSalaryInformation {
      /** Whether the report includes its Salary column(s) */
      showSalaryInformation: boolean
    }
    export namespace WithSalaryInformation {
      export const isWithSalaryInformation: TypeChecker<WithSalaryInformation> =
        TypeChecker.Factory.object({
          showSalaryInformation: TypeChecker.isBoolean,
        })
    }

    /**
     * How the Account List report is generated.
     */
    export interface AccountList {
      /** The file format the report is generated in */
      format: Format
      /** Filters that scope which accounts the report includes */
      filter: AccountList.Filter
    }
    export namespace AccountList {
      /** Filters that scope which accounts a report includes. */
      export interface Filter {
        organizationFilter?: Record.Filter.DimensionFilter
      }
      export namespace Filter {
        export const isFilter: TypeChecker<Filter> = TypeChecker.Factory.object(
          {
            organizationFilter: TypeChecker.Factory.optional(
              Record.Filter.DimensionFilter.isDimensionFilter,
            ),
          },
        )
        export const assertIsFilter: TypeAsserter<Filter> =
          TypeAsserter.Factory.from(isFilter)
      }

      export const isAccountList: TypeChecker<AccountList> =
        TypeChecker.Factory.object({
          filter: Filter.isFilter,
          format: Format.isFormat,
        })
      export const assertIsAccountList: TypeAsserter<AccountList> =
        TypeAsserter.Factory.from(isAccountList)

      /** @see {@link Configuration.getDefault} */
      export const DEFAULT: AccountList = {
        filter: {},
        format: Format.Excel,
      }
    }

    /**
     * How the Data Export report is generated.
     */
    export interface DataExport {
      /**
       * The fields the report includes. The columns keep the report's order,
       * whatever the order here
       *
       * NOTE: empty includes every field
       */
      fields: readonly DataExport.Field[]
      /**
       * The occupant fields the report includes, laid out as
       * {@link occupantLayout} says. The columns keep the report's order,
       * whatever the order here
       *
       * NOTE: empty includes none, so the rooms keep a single row unless the
       * occupants are asked for
       */
      occupantFields: readonly DataExport.OccupantField[]
      /** How the occupants are laid out */
      occupantLayout: DataExport.Layout
      /**
       * The account fields the report includes, laid out as
       * {@link accountLayout} says. The columns keep the report's order,
       * whatever the order here
       *
       * NOTE: empty includes none, so the occupants keep a single row unless
       * the accounts are asked for
       */
      accountFields: readonly DataExport.AccountField[]
      /**
       * How the accounts are laid out. They're the accounts of the row's
       * occupant while the occupants are laid out as
       * {@link DataExport.Layout.Rows}, and the room's otherwise
       */
      accountLayout: DataExport.Layout
      /**
       * The responsible person fields the report includes, laid out as
       * {@link responsiblePersonLayout} says. The columns keep the report's
       * order, whatever the order here
       *
       * NOTE: empty includes none
       */
      responsiblePersonFields: readonly DataExport.ResponsiblePersonField[]
      /** How the responsible persons are laid out */
      responsiblePersonLayout: DataExport.Layout
      /**
       * The functional usage fields the report includes, laid out as
       * {@link functionalUsageLayout} says. The columns keep the report's
       * order, whatever the order here
       *
       * NOTE: empty includes none
       */
      functionalUsageFields: readonly DataExport.FunctionalUsageField[]
      /**
       * How the functional usages are laid out. As
       * {@link DataExport.Layout.Columns}, there's a set of columns per
       * function rather than per functional usage
       */
      functionalUsageLayout: DataExport.Layout
      /** The file format the report is generated in */
      format: Format
      /** Filters that scope which space records the report should include */
      filter: DataExport.Filter
    }
    export namespace DataExport {
      /**
       * A column of the report the user can pick, declared in the order of the
       * report's columns
       *
       * NOTE: there's a code and a name field for every level quiver defines for
       * the space and organization hierarchies, but a setup only has the levels
       * it deploys (see {@link Field.listFor})
       */
      export enum Field {
        CampusCode = 'campusCode',
        CampusName = 'campusName',
        BuildingCode = 'buildingCode',
        BuildingName = 'buildingName',
        FloorCode = 'floorCode',
        FloorName = 'floorName',
        RoomCode = 'roomCode',
        RoomName = 'roomName',
        Status = 'status',
        Area = 'area',
        Description = 'description',
        Comments = 'comments',
        AffiliateCode = 'affiliateCode',
        AffiliateName = 'affiliateName',
        DivisionCode = 'divisionCode',
        DivisionName = 'divisionName',
        DepartmentCode = 'departmentCode',
        DepartmentName = 'departmentName',
        SubdepartmentCode = 'subdepartmentCode',
        SubdepartmentName = 'subdepartmentName',
        RoomTypeCode = 'roomTypeCode',
        RoomTypeName = 'roomTypeName',
        ServiceCenterName = 'serviceCenterName',
        JointUseRoom = 'jointUseRoom',
        ClusterName = 'clusterName',
        LastUpdatedDate = 'lastUpdatedDate',
        LastUpdatedByName = 'lastUpdatedByName',
        LastUpdatedByEmail = 'lastUpdatedByEmail',
        SignedOffDate = 'signedOffDate',
        SignedOffByName = 'signedOffByName',
        SignedOffByEmail = 'signedOffByEmail',
      }
      // Needs to be before namespace is defined
      const FIELDS: ReadonlySet<Field> = new Set(
        Object.values(Field) as Field[],
      )
      export namespace Field {
        // NOTE: also the headers of their columns. The levels' match the names
        // of their quiver entities, which the setup's levels are deployed with.
        // `globalThis.Record` because `Record` refers to the space record entity
        // in this file
        const LABELS: Readonly<globalThis.Record<Field, string>> = {
          [Field.CampusCode]: 'Campus Code',
          [Field.CampusName]: 'Campus Name',
          [Field.BuildingCode]: 'Building Code',
          [Field.BuildingName]: 'Building Name',
          [Field.FloorCode]: 'Floor Code',
          [Field.FloorName]: 'Floor Name',
          [Field.RoomCode]: 'Room Code',
          [Field.RoomName]: 'Room Name',
          [Field.Status]: 'Status',
          [Field.Area]: 'Area (sq. ft.)',
          [Field.Description]: 'Description',
          [Field.Comments]: 'Comments',
          [Field.AffiliateCode]: 'Affiliate Code',
          [Field.AffiliateName]: 'Affiliate Name',
          [Field.DivisionCode]: 'Division Code',
          [Field.DivisionName]: 'Division Name',
          [Field.DepartmentCode]: 'Department Code',
          [Field.DepartmentName]: 'Department Name',
          [Field.SubdepartmentCode]: 'Subdepartment Code',
          [Field.SubdepartmentName]: 'Subdepartment Name',
          [Field.RoomTypeCode]: 'Room Type Code',
          [Field.RoomTypeName]: 'Room Type Name',
          [Field.ServiceCenterName]: 'Service Center Name',
          [Field.JointUseRoom]: 'Joint Use Room',
          [Field.ClusterName]: 'Cluster Name',
          [Field.LastUpdatedDate]: 'Last Updated Date',
          [Field.LastUpdatedByName]: 'Last Updated By Name',
          [Field.LastUpdatedByEmail]: 'Last Updated By Email',
          [Field.SignedOffDate]: 'Signed Off Date',
          [Field.SignedOffByName]: 'Signed Off By Name',
          [Field.SignedOffByEmail]: 'Signed Off By Email',
        }

        /** The code and name fields of a space or organization level */
        export interface OfLevel {
          readonly code: Field
          readonly name: Field
        }

        /** The quiver entities of the space and organization levels */
        const LEVEL_ENTITIES = {
          ...quiver.resources.data.dimensions.space.entities,
          ...quiver.resources.data.dimensions.organization.entities,
        }

        /**
         * A level the report has code and name columns for, by its quiver
         * entity: every one except the room, which the rows are
         */
        type Level = Exclude<keyof typeof LEVEL_ENTITIES, 'room'>

        /**
         * The code and name fields of every level
         *
         * NOTE: `satisfies` keeps a level added to quiver from compiling until
         * it has its fields here, and the template literals keep them named
         * after it
         */
        const LEVEL_FIELDS = {
          affiliate: { code: Field.AffiliateCode, name: Field.AffiliateName },
          building: { code: Field.BuildingCode, name: Field.BuildingName },
          campus: { code: Field.CampusCode, name: Field.CampusName },
          department: {
            code: Field.DepartmentCode,
            name: Field.DepartmentName,
          },
          division: { code: Field.DivisionCode, name: Field.DivisionName },
          floor: { code: Field.FloorCode, name: Field.FloorName },
          subdepartment: {
            code: Field.SubdepartmentCode,
            name: Field.SubdepartmentName,
          },
        } as const satisfies {
          readonly [TLevel in Level]: {
            readonly code: `${TLevel}Code`
            readonly name: `${TLevel}Name`
          }
        }

        /** {@link LEVEL_FIELDS} by the id of the level's quiver entity */
        const BY_LEVEL: ReadonlyMap<core.dms.models.Entity.Id, OfLevel> =
          new Map(
            (Object.keys(LEVEL_FIELDS) as Level[]).map(level => [
              LEVEL_ENTITIES[level].MODEL_ID,
              LEVEL_FIELDS[level],
            ]),
          )

        export const ALL: readonly Field[] = Array.from(FIELDS)
        export const isField = TypeChecker.Factory.oneOf(FIELDS)
        export const assertIsField: TypeAsserter<Field> =
          TypeAsserter.Factory.from(isField)

        export const getLabel = (field: Field): string => LABELS[field]

        /**
         * The code and name fields of the given space or organization level
         *
         * NOTE: throws for an entity that isn't one of the levels, e.g. the
         * room. Every level has its fields, which {@link LEVEL_FIELDS} enforces
         */
        export function getOfLevel(
          entityId: core.dms.models.Entity.Id,
        ): OfLevel {
          const fields = BY_LEVEL.get(entityId)
          if (!fields) {
            throw new errors.NotFoundError({
              id: entityId,
              location: 'Data Export fields',
              type: 'level',
            })
          }
          return fields
        }

        /**
         * The fields of the report in the given setup, in the order of its
         * columns: every field, except the ones of the levels the setup doesn't
         * deploy (e.g. Affiliate)
         */
        export function listFor(schema: core.dms.models.Schema): Field[] {
          const missing = new Set(
            [...BY_LEVEL]
              .filter(([entityId]) => !schema.hasEntity(entityId))
              .flatMap(([, { code, name }]) => [code, name]),
          )
          return ALL.filter(field => !missing.has(field))
        }
      }

      /**
       * A column describing the room's occupant the user can pick, declared in
       * the order of the report's columns, which come after every
       * {@link Field}'s
       */
      export enum OccupantField {
        Code = 'occupantCode',
        FullName = 'occupantFullName',
        Title = 'occupantTitle',
      }
      // Needs to be before namespace is defined
      const OCCUPANT_FIELDS: ReadonlySet<OccupantField> = new Set(
        Object.values(OccupantField) as OccupantField[],
      )
      export namespace OccupantField {
        // NOTE: also the headers of their columns. `globalThis.Record` because
        // `Record` refers to the space record entity in this file
        const LABELS: Readonly<globalThis.Record<OccupantField, string>> = {
          [OccupantField.Code]: 'Occupant Code',
          [OccupantField.FullName]: 'Occupant Full Name',
          [OccupantField.Title]: 'Occupant Title',
        }

        export const ALL: readonly OccupantField[] = Array.from(OCCUPANT_FIELDS)
        export const isOccupantField =
          TypeChecker.Factory.oneOf(OCCUPANT_FIELDS)
        export const assertIsOccupantField: TypeAsserter<OccupantField> =
          TypeAsserter.Factory.from(isOccupantField)

        export const getLabel = (field: OccupantField): string => LABELS[field]
      }

      /**
       * A column describing an account the room's occupant is paid from that
       * the user can pick, declared in the order of the report's columns,
       * which come after every {@link OccupantField}'s
       *
       * NOTE: an account is the project the occupant's payroll is charged to,
       * and only the payrolls assigned to the room count
       */
      export enum AccountField {
        Name = 'accountName',
        Code = 'accountCode',
        RoomPayPercent = 'accountRoomPayPercent',
      }
      // Needs to be before namespace is defined
      const ACCOUNT_FIELDS: ReadonlySet<AccountField> = new Set(
        Object.values(AccountField) as AccountField[],
      )
      export namespace AccountField {
        // NOTE: also the headers of their columns. `globalThis.Record` because
        // `Record` refers to the space record entity in this file
        const LABELS: Readonly<globalThis.Record<AccountField, string>> = {
          [AccountField.Name]: 'Account Name',
          [AccountField.Code]: 'Account Code',
          [AccountField.RoomPayPercent]: 'Percent of Room Pay',
        }

        export const ALL: readonly AccountField[] = Array.from(ACCOUNT_FIELDS)
        export const isAccountField = TypeChecker.Factory.oneOf(ACCOUNT_FIELDS)
        export const assertIsAccountField: TypeAsserter<AccountField> =
          TypeAsserter.Factory.from(isAccountField)

        export const getLabel = (field: AccountField): string => LABELS[field]
      }

      /**
       * A column describing a responsible person of the room that the user can
       * pick, declared in the order of the report's columns, which come after
       * every {@link AccountField}'s
       */
      export enum ResponsiblePersonField {
        Code = 'responsiblePersonCode',
        FullName = 'responsiblePersonFullName',
        Title = 'responsiblePersonTitle',
      }
      // Needs to be before namespace is defined
      const RESPONSIBLE_PERSON_FIELDS: ReadonlySet<ResponsiblePersonField> =
        new Set(
          Object.values(ResponsiblePersonField) as ResponsiblePersonField[],
        )
      export namespace ResponsiblePersonField {
        // NOTE: also the headers of their columns. `globalThis.Record` because
        // `Record` refers to the space record entity in this file
        const LABELS: Readonly<
          globalThis.Record<ResponsiblePersonField, string>
        > = {
          [ResponsiblePersonField.Code]: 'Responsible Person Code',
          [ResponsiblePersonField.FullName]: 'Responsible Person Full Name',
          [ResponsiblePersonField.Title]: 'Responsible Person Title',
        }

        export const ALL: readonly ResponsiblePersonField[] = Array.from(
          RESPONSIBLE_PERSON_FIELDS,
        )
        export const isResponsiblePersonField = TypeChecker.Factory.oneOf(
          RESPONSIBLE_PERSON_FIELDS,
        )
        export const assertIsResponsiblePersonField: TypeAsserter<ResponsiblePersonField> =
          TypeAsserter.Factory.from(isResponsiblePersonField)

        export const getLabel = (field: ResponsiblePersonField): string =>
          LABELS[field]
      }

      /**
       * A column describing a functional usage of the room that the user can
       * pick, declared in the order of the report's columns, which come after
       * every {@link ResponsiblePersonField}'s
       *
       * NOTE: a functional usage is the share of the room a function (e.g.
       * Instruction) uses
       */
      export enum FunctionalUsageField {
        Code = 'functionalUsageCode',
        Name = 'functionalUsageName',
        Percent = 'functionalUsagePercent',
        DecimalPercent = 'functionalUsageDecimalPercent',
        Area = 'functionalUsageArea',
      }
      // Needs to be before namespace is defined
      const FUNCTIONAL_USAGE_FIELDS: ReadonlySet<FunctionalUsageField> =
        new Set(Object.values(FunctionalUsageField) as FunctionalUsageField[])
      export namespace FunctionalUsageField {
        // NOTE: also the headers of their columns. `globalThis.Record` because
        // `Record` refers to the space record entity in this file
        const LABELS: Readonly<
          globalThis.Record<FunctionalUsageField, string>
        > = {
          [FunctionalUsageField.Code]: 'Functional Usage Code',
          [FunctionalUsageField.Name]: 'Functional Usage Name',
          [FunctionalUsageField.Percent]: 'Functional Usage Percent',
          [FunctionalUsageField.DecimalPercent]:
            'Functional Usage Decimal Percent',
          [FunctionalUsageField.Area]: 'Functional Usage Area (sq. ft.)',
        }

        export const ALL: readonly FunctionalUsageField[] = Array.from(
          FUNCTIONAL_USAGE_FIELDS,
        )
        export const isFunctionalUsageField = TypeChecker.Factory.oneOf(
          FUNCTIONAL_USAGE_FIELDS,
        )
        export const assertIsFunctionalUsageField: TypeAsserter<FunctionalUsageField> =
          TypeAsserter.Factory.from(isFunctionalUsageField)

        export const getLabel = (field: FunctionalUsageField): string =>
          LABELS[field]
      }

      /**
       * How a group of fields (e.g. the occupant fields) lays out a room's
       * items (e.g. its occupants)
       */
      export enum Layout {
        /**
         * Down the room's rows: an item per row, the room repeated on each,
         * next to the items of the other groups laid out the same way
         */
        Rows = 'rows',
        /**
         * Across the room's row: a set of the group's columns per item, as many
         * as the room with the most items needs, repeated on each of the room's
         * rows
         */
        Columns = 'columns',
      }
      // Needs to be before namespace is defined
      const LAYOUTS: ReadonlySet<Layout> = new Set(
        Object.values(Layout) as Layout[],
      )
      export namespace Layout {
        // NOTE: `globalThis.Record` because `Record` refers to the space record
        // entity in this file
        const LABELS: Readonly<globalThis.Record<Layout, string>> = {
          [Layout.Rows]: 'Rows',
          [Layout.Columns]: 'Columns',
        }

        export const ALL: readonly Layout[] = Array.from(LAYOUTS)
        export const isLayout = TypeChecker.Factory.oneOf(LAYOUTS)
        export const assertIsLayout: TypeAsserter<Layout> =
          TypeAsserter.Factory.from(isLayout)

        export const getLabel = (layout: Layout): string => LABELS[layout]
      }

      /**
       * Filters that scope which space records a report should include.
       *
       * NOTE: reuses {@link Record.Filter.DimensionFilter}, which is what
       * dimension-filter plugins are already built against.
       */
      export interface Filter {
        clusterFilter?: Record.Filter.DimensionFilter
        organizationFilter?: Record.Filter.DimensionFilter
        spaceFilter?: Record.Filter.DimensionFilter
      }
      export namespace Filter {
        export const isFilter: TypeChecker<Filter> = TypeChecker.Factory.object(
          {
            clusterFilter: TypeChecker.Factory.optional(
              Record.Filter.DimensionFilter.isDimensionFilter,
            ),
            organizationFilter: TypeChecker.Factory.optional(
              Record.Filter.DimensionFilter.isDimensionFilter,
            ),
            spaceFilter: TypeChecker.Factory.optional(
              Record.Filter.DimensionFilter.isDimensionFilter,
            ),
          },
        )
        export const assertIsFilter: TypeAsserter<Filter> =
          TypeAsserter.Factory.from(isFilter)
      }

      export const isDataExport: TypeChecker<DataExport> =
        TypeChecker.Factory.object({
          accountFields: TypeChecker.Factory.list(AccountField.isAccountField),
          accountLayout: Layout.isLayout,
          fields: TypeChecker.Factory.list(Field.isField),
          filter: Filter.isFilter,
          format: Format.isFormat,
          functionalUsageFields: TypeChecker.Factory.list(
            FunctionalUsageField.isFunctionalUsageField,
          ),
          functionalUsageLayout: Layout.isLayout,
          occupantFields: TypeChecker.Factory.list(
            OccupantField.isOccupantField,
          ),
          occupantLayout: Layout.isLayout,
          responsiblePersonFields: TypeChecker.Factory.list(
            ResponsiblePersonField.isResponsiblePersonField,
          ),
          responsiblePersonLayout: Layout.isLayout,
        })
      export const assertIsDataExport: TypeAsserter<DataExport> =
        TypeAsserter.Factory.from(isDataExport)

      /** @see {@link Configuration.getDefault} */
      export const DEFAULT: DataExport = {
        accountFields: [],
        accountLayout: Layout.Columns,
        fields: [],
        filter: {},
        format: Format.Excel,
        functionalUsageFields: [],
        functionalUsageLayout: Layout.Columns,
        occupantFields: [],
        occupantLayout: Layout.Columns,
        responsiblePersonFields: [],
        responsiblePersonLayout: Layout.Columns,
      }
    }

    /**
     * How the Employee List report is generated.
     */
    export interface EmployeeList extends WithSalaryInformation {
      /** The file format the report is generated in */
      format: Format
      /** Filters that scope which payroll entries the report includes */
      filter: EmployeeList.Filter
    }
    export namespace EmployeeList {
      /** Filters that scope which payroll entries a report includes. */
      export interface Filter {
        organizationFilter?: Record.Filter.DimensionFilter
      }
      export namespace Filter {
        export const isFilter: TypeChecker<Filter> = TypeChecker.Factory.object(
          {
            organizationFilter: TypeChecker.Factory.optional(
              Record.Filter.DimensionFilter.isDimensionFilter,
            ),
          },
        )
        export const assertIsFilter: TypeAsserter<Filter> =
          TypeAsserter.Factory.from(isFilter)
      }

      export const isEmployeeList: TypeChecker<EmployeeList> =
        TypeChecker.Factory.object({
          filter: Filter.isFilter,
          format: Format.isFormat,
          showSalaryInformation: TypeChecker.isBoolean,
        })
      export const assertIsEmployeeList: TypeAsserter<EmployeeList> =
        TypeAsserter.Factory.from(isEmployeeList)

      /** @see {@link Configuration.getDefault} */
      export const DEFAULT: EmployeeList = {
        filter: {},
        format: Format.Excel,
        showSalaryInformation: false,
      }
    }

    /**
     * How the Room Links report is generated.
     */
    export interface RoomLinks {
      /** The file format the report is generated in */
      format: Format
    }
    export namespace RoomLinks {
      export const isRoomLinks: TypeChecker<RoomLinks> =
        TypeChecker.Factory.object({
          format: Format.isFormat,
        })
      export const assertIsRoomLinks: TypeAsserter<RoomLinks> =
        TypeAsserter.Factory.from(isRoomLinks)

      /** @see {@link Configuration.getDefault} */
      export const DEFAULT: RoomLinks = {
        format: Format.Excel,
      }
    }

    /**
     * How the Room Supporting Information report is generated.
     *
     * NOTE: its Salary Information is each occupant's Salary column in the
     * Occupants and Accounts block
     */
    export interface RoomSupportingInformation extends WithSalaryInformation {
      /** How the report's rows are ordered */
      sortBy: RoomSupportingInformation.SortBy
      /** The file format the report is generated in */
      format: Format
      /** Filters that scope which records the report should include */
      filter: RoomSupportingInformation.Filter
      /**
       * Whether occupant names in the Occupants and Accounts block include the
       * employee's code
       */
      showEmployeeNumbers: boolean
      /** Whether the S&W Average block is included */
      showCalculatedRoomFunctionalPercentages: boolean
      /**
       * Whether the Occupants and Accounts block includes the Account code/name
       * columns
       */
      showProjectInformation: boolean
    }
    export namespace RoomSupportingInformation {
      export const isRoomSupportingInformation: TypeChecker<RoomSupportingInformation> =
        TypeChecker.Factory.lazy(() =>
          TypeChecker.Factory.object({
            filter: RoomSupportingInformation.Filter.isFilter,
            format: Format.isFormat,
            showProjectInformation: TypeChecker.isBoolean,
            showCalculatedRoomFunctionalPercentages: TypeChecker.isBoolean,
            showEmployeeNumbers: TypeChecker.isBoolean,
            showSalaryInformation: TypeChecker.isBoolean,
            sortBy: RoomSupportingInformation.SortBy.isSortBy,
          }),
        )
      export const assertIsRoomSupportingInformation: TypeAsserter<RoomSupportingInformation> =
        TypeAsserter.Factory.from(isRoomSupportingInformation)

      /** How a report's rows are ordered. */
      export enum SortBy {
        Building = 'building',
        ResponsiblePerson = 'responsiblePerson',
      }
      // Needs to be before namespace is defined
      const SORT_BYS: ReadonlySet<SortBy> = new Set(
        Object.values(SortBy) as SortBy[],
      )
      export namespace SortBy {
        // NOTE: `globalThis.Record` because `Record` refers to the space record
        // entity in this file
        const LABELS: Readonly<globalThis.Record<SortBy, string>> = {
          [SortBy.Building]: 'By Building',
          [SortBy.ResponsiblePerson]: 'By Responsible Person',
        }

        export const ALL: readonly SortBy[] = Array.from(SORT_BYS)
        export const isSortBy = TypeChecker.Factory.oneOf(SORT_BYS)
        export const assertIsSortBy: TypeAsserter<SortBy> =
          TypeAsserter.Factory.from(isSortBy)

        export const getLabel = (sortBy: SortBy): string => LABELS[sortBy]
      }

      /**
       * Filters that scope which records a report should include.
       *
       * NOTE: reuses {@link Record.Filter.DimensionFilter} rather than declaring a
       * separate identical type, since {@link Record.Filter.DimensionFilter} is
       * what dimension-filter plugins are already built against.
       */
      export interface Filter {
        clusterFilter?: Record.Filter.DimensionFilter
        organizationFilter?: Record.Filter.DimensionFilter
        employeeFilter?: Record.Filter.DimensionFilter
        roomTypeFilter?: Record.Filter.DimensionFilter
        spaceFilter?: Record.Filter.DimensionFilter
        /**
         * Functional methodologies the report should cover
         *
         * NOTE: empty means 'Any' — no methodology filtering is applied
         */
        functionalMethodologies: readonly Record.FunctionalUsage.Methodology[]
      }
      export namespace Filter {
        export const isFilter: TypeChecker<Filter> = TypeChecker.Factory.object(
          {
            clusterFilter: TypeChecker.Factory.optional(
              Record.Filter.DimensionFilter.isDimensionFilter,
            ),
            employeeFilter: TypeChecker.Factory.optional(
              Record.Filter.DimensionFilter.isDimensionFilter,
            ),
            functionalMethodologies: TypeChecker.Factory.list(
              Record.FunctionalUsage.Methodology.isMethodology,
            ),
            organizationFilter: TypeChecker.Factory.optional(
              Record.Filter.DimensionFilter.isDimensionFilter,
            ),
            roomTypeFilter: TypeChecker.Factory.optional(
              Record.Filter.DimensionFilter.isDimensionFilter,
            ),
            spaceFilter: TypeChecker.Factory.optional(
              Record.Filter.DimensionFilter.isDimensionFilter,
            ),
          },
        )
        export const assertIsFilter: TypeAsserter<Filter> =
          TypeAsserter.Factory.from(isFilter)
      }

      /** @see {@link Configuration.getDefault} */
      export const DEFAULT: RoomSupportingInformation = {
        filter: { functionalMethodologies: [] },
        format: Format.Excel,
        showProjectInformation: true,
        showCalculatedRoomFunctionalPercentages: true,
        showEmployeeNumbers: true,
        showSalaryInformation: true,
        sortBy: RoomSupportingInformation.SortBy.Building,
      }
    }

    /**
     * How the Rooms by Account report is generated.
     */
    export interface RoomsByAccount {
      /** The file format the report is generated in */
      format: Format
      /** Filters that scope which accounts the report includes */
      filter: RoomsByAccount.Filter
    }
    export namespace RoomsByAccount {
      /** Filters that scope which accounts a report includes. */
      export interface Filter {
        accountFilter?: Record.Filter.DimensionFilter
      }
      export namespace Filter {
        export const isFilter: TypeChecker<Filter> = TypeChecker.Factory.object(
          {
            accountFilter: TypeChecker.Factory.optional(
              Record.Filter.DimensionFilter.isDimensionFilter,
            ),
          },
        )
        export const assertIsFilter: TypeAsserter<Filter> =
          TypeAsserter.Factory.from(isFilter)
      }

      export const isRoomsByAccount: TypeChecker<RoomsByAccount> =
        TypeChecker.Factory.object({
          filter: Filter.isFilter,
          format: Format.isFormat,
        })
      export const assertIsRoomsByAccount: TypeAsserter<RoomsByAccount> =
        TypeAsserter.Factory.from(isRoomsByAccount)

      /** @see {@link Configuration.getDefault} */
      export const DEFAULT: RoomsByAccount = {
        filter: {},
        format: Format.Excel,
      }
    }

    /**
     * How the Rooms by Occupant report is generated.
     */
    export interface RoomsByOccupant {
      /** The file format the report is generated in */
      format: Format
      /** Filters that scope which occupants the report includes */
      filter: RoomsByOccupant.Filter
    }
    export namespace RoomsByOccupant {
      /** Filters that scope which occupants a report includes. */
      export interface Filter {
        employeeFilter?: Record.Filter.DimensionFilter
      }
      export namespace Filter {
        export const isFilter: TypeChecker<Filter> = TypeChecker.Factory.object(
          {
            employeeFilter: TypeChecker.Factory.optional(
              Record.Filter.DimensionFilter.isDimensionFilter,
            ),
          },
        )
        export const assertIsFilter: TypeAsserter<Filter> =
          TypeAsserter.Factory.from(isFilter)
      }

      export const isRoomsByOccupant: TypeChecker<RoomsByOccupant> =
        TypeChecker.Factory.object({
          filter: Filter.isFilter,
          format: Format.isFormat,
        })
      export const assertIsRoomsByOccupant: TypeAsserter<RoomsByOccupant> =
        TypeAsserter.Factory.from(isRoomsByOccupant)

      /** @see {@link Configuration.getDefault} */
      export const DEFAULT: RoomsByOccupant = {
        filter: {},
        format: Format.Excel,
      }
    }

    /**
     * How the Rooms by Responsible Person report is generated.
     */
    export interface RoomsByResponsiblePerson {
      /** The file format the report is generated in */
      format: Format
      /** Filters that scope which responsible persons the report includes */
      filter: RoomsByResponsiblePerson.Filter
    }
    export namespace RoomsByResponsiblePerson {
      /** Filters that scope which responsible persons a report includes. */
      export interface Filter {
        employeeFilter?: Record.Filter.DimensionFilter
      }
      export namespace Filter {
        export const isFilter: TypeChecker<Filter> = TypeChecker.Factory.object(
          {
            employeeFilter: TypeChecker.Factory.optional(
              Record.Filter.DimensionFilter.isDimensionFilter,
            ),
          },
        )
        export const assertIsFilter: TypeAsserter<Filter> =
          TypeAsserter.Factory.from(isFilter)
      }

      export const isRoomsByResponsiblePerson: TypeChecker<RoomsByResponsiblePerson> =
        TypeChecker.Factory.object({
          filter: Filter.isFilter,
          format: Format.isFormat,
        })
      export const assertIsRoomsByResponsiblePerson: TypeAsserter<RoomsByResponsiblePerson> =
        TypeAsserter.Factory.from(isRoomsByResponsiblePerson)

      /** @see {@link Configuration.getDefault} */
      export const DEFAULT: RoomsByResponsiblePerson = {
        filter: {},
        format: Format.Excel,
      }
    }

    /**
     * How the Space/Salary Comparison report is generated.
     */
    export interface SpaceSalaryComparison {
      /** The file format the report is generated in */
      format: Format
      /** Filters that scope which departments the report includes */
      filter: SpaceSalaryComparison.Filter
    }
    export namespace SpaceSalaryComparison {
      /** Filters that scope which departments a report includes. */
      export interface Filter {
        organizationFilter?: Record.Filter.DimensionFilter
      }
      export namespace Filter {
        export const isFilter: TypeChecker<Filter> = TypeChecker.Factory.object(
          {
            organizationFilter: TypeChecker.Factory.optional(
              Record.Filter.DimensionFilter.isDimensionFilter,
            ),
          },
        )
        export const assertIsFilter: TypeAsserter<Filter> =
          TypeAsserter.Factory.from(isFilter)
      }

      export const isSpaceSalaryComparison: TypeChecker<SpaceSalaryComparison> =
        TypeChecker.Factory.object({
          filter: Filter.isFilter,
          format: Format.isFormat,
        })
      export const assertIsSpaceSalaryComparison: TypeAsserter<SpaceSalaryComparison> =
        TypeAsserter.Factory.from(isSpaceSalaryComparison)

      /** @see {@link Configuration.getDefault} */
      export const DEFAULT: SpaceSalaryComparison = {
        filter: {},
        format: Format.Excel,
      }
    }

    /**
     * How the Unassigned Sponsored Accounts report is generated.
     */
    export interface UnassignedSponsoredAccounts extends WithSalaryInformation {
      /** The file format the report is generated in */
      format: Format
      /** Filters that scope which accounts the report should include */
      filter: UnassignedSponsoredAccounts.Filter
    }
    export namespace UnassignedSponsoredAccounts {
      /** Filters that scope which accounts a report should include. */
      export interface Filter {
        organizationFilter?: Record.Filter.DimensionFilter
      }
      export namespace Filter {
        export const isFilter: TypeChecker<Filter> = TypeChecker.Factory.object(
          {
            organizationFilter: TypeChecker.Factory.optional(
              Record.Filter.DimensionFilter.isDimensionFilter,
            ),
          },
        )
        export const assertIsFilter: TypeAsserter<Filter> =
          TypeAsserter.Factory.from(isFilter)
      }

      export const isUnassignedSponsoredAccounts: TypeChecker<UnassignedSponsoredAccounts> =
        TypeChecker.Factory.object({
          filter: Filter.isFilter,
          format: Format.isFormat,
          showSalaryInformation: TypeChecker.isBoolean,
        })
      export const assertIsUnassignedSponsoredAccounts: TypeAsserter<UnassignedSponsoredAccounts> =
        TypeAsserter.Factory.from(isUnassignedSponsoredAccounts)

      /** @see {@link Configuration.getDefault} */
      export const DEFAULT: UnassignedSponsoredAccounts = {
        filter: {},
        format: Format.Excel,
        showSalaryInformation: true,
      }
    }

    /**
     * How the Unassigned Sponsored People report is generated.
     */
    export interface UnassignedSponsoredPeople extends WithSalaryInformation {
      /** The file format the report is generated in */
      format: Format
      /** Filters that scope which people the report should include */
      filter: UnassignedSponsoredPeople.Filter
    }
    export namespace UnassignedSponsoredPeople {
      /** Filters that scope which people a report should include. */
      export interface Filter {
        organizationFilter?: Record.Filter.DimensionFilter
      }
      export namespace Filter {
        export const isFilter: TypeChecker<Filter> = TypeChecker.Factory.object(
          {
            organizationFilter: TypeChecker.Factory.optional(
              Record.Filter.DimensionFilter.isDimensionFilter,
            ),
          },
        )
        export const assertIsFilter: TypeAsserter<Filter> =
          TypeAsserter.Factory.from(isFilter)
      }

      export const isUnassignedSponsoredPeople: TypeChecker<UnassignedSponsoredPeople> =
        TypeChecker.Factory.object({
          filter: Filter.isFilter,
          showSalaryInformation: TypeChecker.isBoolean,
          format: Format.isFormat,
        })
      export const assertIsUnassignedSponsoredPeople: TypeAsserter<UnassignedSponsoredPeople> =
        TypeAsserter.Factory.from(isUnassignedSponsoredPeople)

      /** @see {@link Configuration.getDefault} */
      export const DEFAULT: UnassignedSponsoredPeople = {
        filter: {},
        showSalaryInformation: true,
        format: Format.Excel,
      }
    }

    export interface ProgressByOrganization {
      format: Format
      entityId: core.dms.models.Entity.Id
    }
    export namespace ProgressByOrganization {
      export const isProgressByOrganization: TypeChecker<ProgressByOrganization> =
        TypeChecker.Factory.object({
          format: Format.isFormat,
          entityId: core.dms.models.Entity.Id.isId,
        })
      export const assertIsProgressByOrganization: TypeAsserter<ProgressByOrganization> =
        TypeAsserter.Factory.from(isProgressByOrganization)

      /** @see {@link Configuration.getDefault} */
      export const DEFAULT: ProgressByOrganization = {
        format: Format.Excel,
        entityId:
          quiver.resources.data.dimensions.organization.entities.department
            .MODEL_ID,
      }
    }

    /**
     * How the Unclaimed Rooms report is generated.
     */
    export interface UnclaimedRooms {
      /** The file format the report is generated in */
      format: Format
    }
    export namespace UnclaimedRooms {
      export const isUnclaimedRooms: TypeChecker<UnclaimedRooms> =
        TypeChecker.Factory.object({
          format: Format.isFormat,
        })
      export const assertIsUnclaimedRooms: TypeAsserter<UnclaimedRooms> =
        TypeAsserter.Factory.from(isUnclaimedRooms)

      /** @see {@link Configuration.getDefault} */
      export const DEFAULT: UnclaimedRooms = {
        format: Format.Excel,
      }
    }

    // NOTE: must appear after all of these TypeCheckers have been defined
    const STRICT_TYPE_CHECKERS: {
      [TId in Id]: TypeChecker<Configuration<TId>>
    } = {
      [Id.AccountList]: AccountList.isAccountList,
      [Id.DataExport]: DataExport.isDataExport,
      [Id.EmployeeList]: EmployeeList.isEmployeeList,
      [Id.RoomLinks]: RoomLinks.isRoomLinks,
      [Id.RoomSupportingInformation]:
        RoomSupportingInformation.isRoomSupportingInformation,
      [Id.RoomsByAccount]: RoomsByAccount.isRoomsByAccount,
      [Id.RoomsByOccupant]: RoomsByOccupant.isRoomsByOccupant,
      [Id.RoomsByResponsiblePerson]:
        RoomsByResponsiblePerson.isRoomsByResponsiblePerson,
      [Id.SpaceSalaryComparison]: SpaceSalaryComparison.isSpaceSalaryComparison,
      [Id.UnassignedSponsoredAccounts]:
        UnassignedSponsoredAccounts.isUnassignedSponsoredAccounts,
      [Id.UnassignedSponsoredPeople]:
        UnassignedSponsoredPeople.isUnassignedSponsoredPeople,
      [Id.ProgressByOrganization]:
        ProgressByOrganization.isProgressByOrganization,
      [Id.UnclaimedRooms]: UnclaimedRooms.isUnclaimedRooms,
    }

    // NOTE: must appear after all of these configurations have been defined
    const DEFAULTS: { [TId in Id]: Configuration<TId> } = {
      [Id.AccountList]: AccountList.DEFAULT,
      [Id.DataExport]: DataExport.DEFAULT,
      [Id.EmployeeList]: EmployeeList.DEFAULT,
      [Id.RoomLinks]: RoomLinks.DEFAULT,
      [Id.RoomSupportingInformation]: RoomSupportingInformation.DEFAULT,
      [Id.RoomsByAccount]: RoomsByAccount.DEFAULT,
      [Id.RoomsByOccupant]: RoomsByOccupant.DEFAULT,
      [Id.RoomsByResponsiblePerson]: RoomsByResponsiblePerson.DEFAULT,
      [Id.SpaceSalaryComparison]: SpaceSalaryComparison.DEFAULT,
      [Id.UnassignedSponsoredAccounts]: UnassignedSponsoredAccounts.DEFAULT,
      [Id.UnassignedSponsoredPeople]: UnassignedSponsoredPeople.DEFAULT,
      [Id.ProgressByOrganization]: ProgressByOrganization.DEFAULT,
      [Id.UnclaimedRooms]: UnclaimedRooms.DEFAULT,
    }
  }
}
